//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import getter from 'dibs-object-getter';

import styles from './ItemTagTooltip.scss';

import { Tooltip } from 'dibs-elements/exports/Tooltip';

import type { ItemTagTooltip_tag as TradePhotoTag } from './__generated__/ItemTagTooltip_tag.graphql';

type ItemTagTooltipProps = {
    tag: ?TradePhotoTag,
    isVisible: boolean,
    onClose: () => void,
    tooltipDirection: 'top' | 'left' | 'right' | 'bottom',
};

export const MAX_LENGTH = 40;

export function getText(input: ?string): ?string {
    if (!input) {
        return null;
    }

    if (input.length <= MAX_LENGTH) {
        return input;
    }

    const substring = input.slice(0, MAX_LENGTH);

    return `${substring}...`;
}

export function ItemTagTooltipComponent(props: ItemTagTooltipProps) {
    const getTag = getter(props.tag);

    const name = getText(getTag.value('name'));
    const creator = getText(
        getTag
            .at('similarItems')
            .at('creator')
            .value('name')
    );
    const category = getText(
        getTag
            .at('similarItems')
            .at('category')
            .value('name')
    );
    const link = getTag.at('similarItems').value('searchUrl');

    // istanbul ignore next
    const _mouseLeave = (event: SyntheticMouseEvent<HTMLElement>) => {
        // if visible and leaving the box in any direction but bottom; close
        if (!props.isVisible) {
            return;
        }

        const { bottom, left, right } = event.currentTarget.getBoundingClientRect();
        const { pageX, pageY } = event;
        const { scrollY, scrollX } = window;

        const belowTooltopY = pageY >= bottom + scrollY;

        const division = (right - left) / 2;
        const centerX = division + left + scrollX;
        const aboveIconX = pageX >= centerX - 20 && pageX <= centerX + 20;

        if (belowTooltopY && aboveIconX) {
            return;
        }
        props.onClose();
    };

    return (
        <Tooltip isVisible={props.isVisible} direction={props.tooltipDirection}>
            <div onMouseLeave={_mouseLeave} className={styles.wrapper} data-tn="item-tag-content">
                <div className={styles.tagName} data-tn="item-tag-name">
                    {name}
                </div>
                <div className={styles.categoryCreator} data-tn="item-tag-creator">
                    {category} {creator ? ` • ${creator}` : ''}
                </div>
                {link && (
                    <a href={link} className={styles.searchLink} data-tn="view-similar-link">
                        <FormattedMessage
                            id="interiorPhoto.tag.viewSimilar"
                            defaultMessage="View Similar Pieces"
                        />
                    </a>
                )}
            </div>
        </Tooltip>
    );
}

export const ItemTagTooltip = createFragmentContainer(ItemTagTooltipComponent, {
    tag: graphql`
        fragment ItemTagTooltip_tag on TradePhotoTag {
            name
            similarItems {
                creator {
                    name
                }
                category {
                    name
                }
                searchUrl
            }
        }
    `,
});
